/* eslint-disable @typescript-eslint/no-explicit-any */
import { collection, getDocs, DocumentData, doc, getDoc, DocumentReference } from 'firebase/firestore';
import { initializeFirestore } from 'src/utils/firebaseUtils';
import { logInfoToConsole } from 'src/utils/loggingUtils';

/**
 * Fetch all documents from a collection
 * @param collectionName The name of the Firestore collection
 * @returns An array of documents from the collection
 */
export const getCollection = async <T = DocumentData>(collectionName: string): Promise<T[]> => {
    const db = initializeFirestore();

    const collectionRef = collection(db, collectionName);
    const snapshot = await getDocs(collectionRef);

    return snapshot.docs.map((document) => ({
        id: document.id,
        ...document.data(),
    })) as T[];
};

/**
 * Helper to resolve a single DocumentReference
 * @param refValue Reference value
 * @param refField Field name
 * @returns Resolved field and its value
 */
const resolveSingleReference = async (refValue: DocumentReference, refField: string) => {
    try {
        const refDoc = await getDoc(refValue);

        if (refDoc.exists()) {
            return {
                field: refField,
                value: {
                    id: refDoc.id,
                    ...refDoc.data(),
                },
            };
        }
        logInfoToConsole(`Reference document not found: ${refValue.path}`);
    } catch (error) {
        logInfoToConsole(`Error resolving reference ${refValue.path}:`, error);
    }
    return null;
};

/**
 * Helper to resolve an array of DocumentReferences
 * @param refValue Array of reference values
 * @param refField Field name
 * @param documentId Document ID
 * @returns Resolved field and its value
 */
const resolveReferenceArray = async (refValue: any[], refField: string, documentId: string) => {
    logInfoToConsole(
        `Resolving array of ${refValue.length} references for field '${refField}' in document ${documentId}`,
    );

    const resolvedRefs = await Promise.all(
        refValue.map(async (item, index) => {
            if (item instanceof DocumentReference) {
                try {
                    const refDoc = await getDoc(item);
                    if (refDoc.exists()) {
                        logInfoToConsole(`  ✓ Resolved reference ${index + 1}/${refValue.length}: ${item.path}`);
                        return {
                            id: refDoc.id,
                            ...refDoc.data(),
                        };
                    }
                    logInfoToConsole(`  ✗ Reference ${index + 1}/${refValue.length} not found: ${item.path}`);
                } catch (error) {
                    logInfoToConsole(
                        `  ✗ Error resolving reference ${index + 1}/${refValue.length} (${item.path}):`,
                        error,
                    );
                }
            } else {
                logInfoToConsole(`  ⚠ Item ${index + 1}/${refValue.length} is not a DocumentReference:`, item);
            }
            return null;
        }),
    );

    const filteredRefs = resolvedRefs.filter(Boolean);
    logInfoToConsole(`  → Resolved ${filteredRefs.length}/${refValue.length} references for '${refField}'`);

    return {
        field: refField,
        value: filteredRefs,
    };
};

/**
 * Helper function to resolve reference fields
 * @param data The document data
 * @param refFields Array of field names that contain references
 * @returns Array of resolved fields
 */
const resolveReferenceFields = async (data: any, refFields: string[]) => {
    const resolvedFields = await Promise.all(
        refFields.map((refField) => {
            const refValue = data[refField];

            // Handle single DocumentReference
            if (refValue && refValue instanceof DocumentReference) {
                return resolveSingleReference(refValue, refField);
            }
            // Handle array of DocumentReferences
            else if (Array.isArray(refValue)) {
                return resolveReferenceArray(refValue, refField, data.id);
            }

            return null;
        }),
    );

    return resolvedFields;
};

/**
 * Fetch collection and resolve reference fields (supports single refs and arrays of refs)
 * @param collectionName The name of the Firestore collection
 * @param refFields Array of field names that contain references
 * @returns An array of documents with resolved references
 */
export const getCollectionWithRefs = async <T = DocumentData>(
    collectionName: string,
    refFields: string[] = [],
): Promise<T[]> => {
    const db = initializeFirestore();
    const collectionRef = collection(db, collectionName);
    const snapshot = await getDocs(collectionRef);

    const promises = snapshot.docs.map(async (docSnapshot) => {
        const data: any = { id: docSnapshot.id, ...docSnapshot.data() };

        // Resolve all reference fields in parallel
        const resolvedFields = await resolveReferenceFields(data, refFields);

        // Apply resolved fields to data
        resolvedFields.forEach((resolved) => {
            if (resolved) {
                data[resolved.field] = resolved.value;
            }
        });

        return data as T;
    });

    return Promise.all(promises);
};

/**
 * Get a single document and resolve its references (supports single refs and arrays of refs)
 * @param collectionName The name of the Firestore collection
 * @param documentId The document ID
 * @param refFields Array of field names that contain references
 * @returns The document with resolved references or null if not found
 */
export const getDocumentWithRefs = async <T = DocumentData>(
    collectionName: string,
    documentId: string,
    refFields: string[] = [],
): Promise<T | null> => {
    const db = initializeFirestore();
    const docRef = doc(db, collectionName, documentId);
    const docSnapshot = await getDoc(docRef);

    if (!docSnapshot.exists()) {
        return null;
    }

    const data: any = { id: docSnapshot.id, ...docSnapshot.data() };

    // Resolve all reference fields in parallel
    const resolvedFields = await resolveReferenceFields(data, refFields);

    // Apply resolved fields to data
    resolvedFields.forEach((resolved) => {
        if (resolved) {
            data[resolved.field] = resolved.value;
        }
    });

    return data as T;
};
